/**
 * MarqueeBanner
 * @description Web component used to create sliders, configurable with data-options attribute.
 * @documentation https://swiperjs.com/swiper-api
 */

import { Swiper } from 'swiper';
import { A11y, FreeMode, Autoplay } from 'swiper/modules';

class MarqueeBanner extends HTMLElement {
  constructor() {
    super();
    this.swiper = this.querySelector('[data-swiper]');

    // Stop when the swiper is not found
    if (!this.swiper) {
      return;
    }

    // Default to improve user experience
    this.swiperOptions = {
      modules: [A11y, FreeMode, Autoplay],
      threshold: 10,
      direction: 'horizontal',
      freeMode: true,
      freeModeSticky: false,
      loop: true,
      centeredSlides: true,
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 0,
    };

    // Check if we have extra options on the HTML
    if (this.swiper.dataset.options) {
      const options = JSON.parse(this.swiper.dataset.options);
      if (options) {
        this.swiperOptions = {
          ...this.swiperOptions,
          ...options,
        };
      }
    }

    // Call swiper with selected swiper element and options
    this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

    /**
     * Listen to extra events when in Shopify editor
     */
    if (Shopify.designMode) {
      // Update the swiper when the section event is triggerd
      window.addEventListener('shopify:section:load', (event) => {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
      });

      // When on block select go to the slide in front-end
      window.addEventListener('shopify:block:select', (event) => {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
        this.handleBlockSelect(event);
      });

      // When on block deselect go to the slide in front-end
      window.addEventListener('shopify:block:deselect', (event) => {
        this.swiperInstance.update();
        if (this.swiperOptions.navigation) {
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        }
        this.handleBlockSelect(event);
      });
    }
  }

  /**
   * Handles the theme editor block change/edit event
   * @param {Object} event
   */
  handleBlockSelect(event) {
    // Check if the slide index is set
    if (!('swiperSlideIndex' in event.target.dataset)) {
      return;
    }

    // Set the slide index based on loop settings or not
    let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
    if (this.swiperOptions.loop) {
      swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
    }

    // Slide to slide based on the data attribute from the target
    const sliderToUpdate = event.target.closest('[data-swiper]');
    sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
  }
}

if (!customElements.get('marquee-banner')) {
  window.MarqueeBanner = MarqueeBanner;
  customElements.define('marquee-banner', MarqueeBanner);
}
